/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, createContext, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Box, Button, Flex, Text, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import LoadingSpinner from '../loading-spinner'

const ToggleCardContext = createContext()

/**
 * A card-like box that renders one of two states: 'edit' and 'summary'. It takes a single
 * `ToggleCardSummary` and `ToggleCardEdit` component as children and renders one or the
 * other depending on the `editing` prop. See `app/pages/checkout` for example.
 */
export const ToggleCard = ({
    id,
    title,
    asHeading = 'h2',
    headingProps = {},
    headerStyle = 'eyeBrowSmall',
    editing,
    disabled,
    disableEdit,
    onEdit,
    editLabel,
    isLoading,
    children,
    // off focus on title if required to focus on other element, e.g.: the first form field
    isTitleFocusOn = true,
    isTitleDisabledWhenNotEditing = false,
    ...props
}) => {
    const titleRef = useRef()

    useEffect(() => {
        if (isTitleFocusOn && editing && titleRef.current) {
            titleRef.current.focus()
        }
    }, [editing, isTitleFocusOn])

    return (
        <ToggleCardContext.Provider value={{editing, disabled}}>
            <Box
                layerStyle="card"
                data-testid={`sf-toggle-card-${id}`}
                position="relative"
                {...props}
            >
                <Stack spacing={editing ? 24 : 16}>
                    <Flex
                        justify="space-between"
                        alignItems="center"
                        gap={10}
                        textStyle={headerStyle}
                    >
                        <Text
                            as={asHeading}
                            ref={titleRef}
                            tabIndex="0"
                            textStyle={headerStyle}
                            textTransform="uppercase"
                            {...headingProps}
                            color={
                                isTitleDisabledWhenNotEditing && disabled && !editing
                                    ? 'dimGray'
                                    : 'fullBlack'
                            }
                        >
                            {title}
                        </Text>
                        {!editing && !disabled && onEdit && !disableEdit && (
                            <Box>
                                <Button variant="link" onClick={onEdit} aria-label={editLabel}>
                                    {editLabel || (
                                        <FormattedMessage
                                            defaultMessage="Edit"
                                            id="toggle_card.action.edit"
                                        />
                                    )}
                                </Button>
                            </Box>
                        )}
                    </Flex>
                    <Box data-testid={`sf-toggle-card-${id}-content`} _empty={{display: 'none'}}>
                        {children}
                    </Box>
                </Stack>

                {isLoading && editing && <LoadingSpinner />}
            </Box>
        </ToggleCardContext.Provider>
    )
}

/**
 * ToggleCardEdit is a functional component that conditionally renders its children
 * based on the editing state provided by the ToggleCardContext.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components or elements to be conditionally rendered.
 *
 * @returns {React.ReactNode|null} - Returns the children if the editing state is true, otherwise returns null.
 *
 * This component should be used within a ToggleCardContext provider to access the editing state.
 */
export const ToggleCardEdit = ({children}) => {
    const {editing} = useContext(ToggleCardContext)
    return editing ? children : null
}

/**
 * ToggleCardSummary is a functional component that conditionally renders its children
 * based on the current state of the ToggleCardContext.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be conditionally rendered.
 *
 * @returns {React.ReactNode|null} The children elements if the ToggleCard is not in
 * editing mode and not disabled; otherwise, returns null.
 *
 * This component utilizes the ToggleCardContext to determine whether the card is in
 * editing mode or disabled. If either of these conditions is true, the children will
 * not be rendered.
 */
export const ToggleCardSummary = ({children}) => {
    const {editing, disabled} = useContext(ToggleCardContext)
    return !editing && !disabled ? children : null
}

ToggleCard.propTypes = {
    id: PropTypes.string,
    title: PropTypes.any,
    asHeading: PropTypes.string,
    headingProps: PropTypes.object,
    headerStyle: PropTypes.string,
    editLabel: PropTypes.any,
    editing: PropTypes.bool,
    isLoading: PropTypes.bool,
    disabled: PropTypes.bool,
    disableEdit: PropTypes.bool,
    onEdit: PropTypes.func,
    children: PropTypes.any,
    isTitleFocusOn: PropTypes.bool,
    isTitleDisabledWhenNotEditing: PropTypes.bool
}

ToggleCardEdit.propTypes = {
    children: PropTypes.any
}

ToggleCardSummary.propTypes = {
    children: PropTypes.any
}
